import { useEffect, useMemo, useState } from 'react';
import api from '../api/endpoints';
import { Chip } from './Ui';

/**
 * Multi-select for the categories a business is listed under. The starred one
 * is the primary category; click the star on another to move it. Only
 * categories open to this partner type are offered.
 */
const CategoryPicker = ({
  value = [], primary, onChange, partnerType, label = 'Categories', max = 15,
  known = [],   // category objects already on the profile - names for chips the list may not contain
}) => {
  const [all, setAll] = useState([]);
  const [names, setNames] = useState({});
  const [q, setQ] = useState('');

  // Remember every category we have seen, so a selected chip always shows its
  // name - even after the suggestion list changes with the partner's types.
  const remember = (list) => setNames((m) => {
    const next = { ...m };
    (list || []).forEach((c) => { if (c && c._id && c.name) next[String(c._id)] = c; });
    return next;
  });

  useEffect(() => { remember(known); }, [known]);
  useEffect(() => {
    api.categories.list({ limit: 300 }).then((r) => remember(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.categories.list(partnerType ? { partnerType, limit: 200 } : { limit: 300 })
      .then(async (r) => {
        let list = Array.isArray(r.data) ? r.data : [];
        // Nothing for this mix of types (or an older server): offer everything instead.
        if (partnerType && !list.length) list = (await api.categories.list({ limit: 300 })).data || [];
        setAll(list);
        remember(list);
      })
      .catch(() => {});
  }, [partnerType]);

  const ids = value.map((c) => String((c && c._id) || c));
  const star = String((primary && primary._id) || primary || ids[0] || '');
  const byId = useMemo(() => new Map(Object.entries(names)), [names]);
  const shown = all
    .filter((c) => !ids.includes(String(c._id)))
    .filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()))
    .slice(0, q ? 40 : 18);

  const emit = (next, nextPrimary = star) => onChange({
    categories: next,
    primaryCategory: next.includes(nextPrimary) ? nextPrimary : next[0] || null,
  });

  return (
    <div style={{ marginBottom: 16, display: 'grid', gap: 8 }}>
      <div className="t-small" style={{ fontWeight: 700 }}>{label}</div>
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        {ids.map((id) => (
          <span key={id} className="chip active" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button type="button" title="Make main category" onClick={() => emit(ids, id)}
              style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 15 }}>
              {star === id ? '★' : '☆'}
            </button>
            {(byId.get(id) && byId.get(id).name) || 'Category'}
            <button type="button" aria-label="Remove" onClick={() => emit(ids.filter((x) => x !== id))}
              style={{ background: 'none', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, fontSize: 16, fontWeight: 800 }}>
              ×
            </button>
          </span>
        ))}
        {!ids.length ? <span className="t-small">Pick at least one.</span> : null}
      </div>
      <input className="input" placeholder="Search categories" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        {shown.map((c) => (
          <Chip key={c._id} label={`+ ${c.icon ? `${c.icon} ` : ''}${c.name}`}
            onClick={() => ids.length < max && emit([...ids, String(c._id)])} />
        ))}
      </div>
      <div className="t-tiny">★ marks your main category. Up to {max}.</div>
    </div>
  );
};

export default CategoryPicker;
